import { useMemo } from 'react';
import { SeriesConfig, DeltaConfig } from '../types';

/**
 * Type representing the delta information for each series.
 */
export type DeltaInfo = {
  [key: string]: {
    delta: number;
  } & Partial<DeltaConfig>;
};

/**
 * Hook to compute delta values for chart data.
 *
 * @template TData - The type of data items.
 *
 * @param data - The original data array.
 * @param series - The series configurations.
 * @param deltaConfig - Configuration for delta calculations.
 *
 * @returns A new data array with delta information appended to each data point.
 */
export function useChartDataWithDelta<TData>(
  data: TData[],
  series: readonly SeriesConfig<TData, unknown>[],
  deltaConfig: DeltaConfig = { showDelta: false },
): (TData & { delta?: DeltaInfo })[] {
  return useMemo(() => {
    if (!deltaConfig.showDelta) {
      return data as (TData & { delta?: DeltaInfo })[];
    }

    return data.map((item, index) => {
      if (index === 0) {
        return item as TData & { delta?: DeltaInfo };
      }

      const previousItem = data[index - 1];
      const delta = calculateDelta(item, previousItem, series, deltaConfig);

      return {
        ...item,
        delta: Object.keys(delta).length > 0 ? delta : undefined,
        ...deltaConfig,
      };
    });
  }, [data, series, deltaConfig]);
}

function calculateDelta<TData>(
  currentItem: TData,
  previousItem: TData,
  normalizedSeries: readonly SeriesConfig<TData, unknown>[],
  deltaConfig: DeltaConfig,
): DeltaInfo {
  const delta: DeltaInfo = {};

  normalizedSeries.forEach((seriesConfig) => {
    const dataKey = seriesConfig.dataKey as Extract<keyof TData, string>;
    const currentValue = currentItem[dataKey];
    const previousValue = previousItem[dataKey];

    if (typeof currentValue === 'number' && typeof previousValue === 'number') {
      const computedDelta = currentValue - previousValue;

      delta[dataKey] = {
        delta: computedDelta,
        ...deltaConfig,
      };
    }
  });

  return delta;
}
